import { Money } from '@shared/lib/money/money'
import type { MonetaryValue } from '@shared/lib/money/money'
import type { SaleListItem } from './sale.contract'

export interface SaleJourney {
  readonly sale: SaleListItem
  readonly children: SaleListItem[]
}

export interface JourneySummary {
  readonly total: MonetaryValue
  readonly originalTotal: MonetaryValue
  readonly affectedTotal: MonetaryValue
  readonly hasFinancialImpact: boolean
  readonly itemCount: number
  readonly embeddedItemCount: number
  readonly separateChargeCount: number
  readonly pendingStages: number
}

export interface JourneyStatusSummary {
  readonly label: string
  readonly mixed: boolean
  readonly chargeCount: number
}

export interface SaleAmountImpact {
  readonly active: MonetaryValue
  readonly original: MonetaryValue
  readonly affected: MonetaryValue
  readonly hasImpact: boolean
}

const STATUS_LABELS: Record<string, string> = {
  PAID: 'paga',
  PENDING: 'pendente',
  FAILED: 'recusada',
  REFUND_REQUESTED: 'aguardando reembolso',
  REFUNDED: 'estorno',
  CHARGED_BACK: 'chargeback',
  PARTIAL_REFUND: 'reembolso parcial',
  PREVENTED_CHARGEBACK: 'reembolso de chargeback',
  DISPUTE_WON: 'disputa ganha',
  SUBSCRIPTION_CANCELED: 'assinatura encerrada',
}

const PLURAL_STATUS_LABELS: Record<string, string> = {
  PAID: 'pagas',
  PENDING: 'pendentes',
  FAILED: 'recusadas',
  REFUND_REQUESTED: 'aguardando reembolso',
  REFUNDED: 'estornadas',
  CHARGED_BACK: 'em chargeback',
  PARTIAL_REFUND: 'com reembolso parcial',
  PREVENTED_CHARGEBACK: 'reembolsos de chargeback',
  DISPUTE_WON: 'disputas ganhas',
  SUBSCRIPTION_CANCELED: 'assinaturas encerradas',
}

export function chargeStateKey(sale: SaleListItem): string {
  const latest = sale.financialEvents?.filter((event) => event.kind !== 'FX_LOSS').at(-1)
  if (latest?.kind === 'PARTIAL_REFUND') return 'PARTIAL_REFUND'
  if (latest?.kind === 'DISPUTE_WON' && sale.status !== 'CHARGED_BACK') return 'DISPUTE_WON'
  if (sale.status === 'REFUNDED' && sale.handledByGlobalChargeback) {
    return 'PREVENTED_CHARGEBACK'
  }
  if (sale.status === 'PAID' && sale.subscriptionStatus === 'CANCELED') {
    return 'SUBSCRIPTION_CANCELED'
  }
  return sale.status
}

function countedStatusLabel(status: string, count: number): string {
  return `${count} ${count === 1 ? (STATUS_LABELS[status] ?? status.toLocaleLowerCase('pt-BR')) : (PLURAL_STATUS_LABELS[status] ?? status.toLocaleLowerCase('pt-BR'))}`
}

export function summarizeJourneyStatuses(journey: SaleJourney): JourneyStatusSummary {
  const charges = [journey.sale, ...journey.children]
  const counts = new Map<string, number>()
  for (const charge of charges) {
    const state = chargeStateKey(charge)
    counts.set(state, (counts.get(state) ?? 0) + 1)
  }
  return {
    label: [...counts.entries()]
      .map(([status, count]) => countedStatusLabel(status, count))
      .join(' · '),
    mixed: counts.size > 1,
    chargeCount: charges.length,
  }
}

export function receivedAmountOf(sale: SaleListItem): MonetaryValue {
  return sale.grossAmount ?? sale.amount
}

export function activeAmountOf(sale: SaleListItem): MonetaryValue {
  return summarizeSaleAmountImpact(sale).active
}

export function summarizeSaleAmountImpact(sale: SaleListItem): SaleAmountImpact {
  const gross = Money.fromMonetaryValue(receivedAmountOf(sale))
  if (!wasCharged(sale)) {
    return {
      active: new Money(0, gross.currency).toMonetaryValue(),
      original: gross.toMonetaryValue(),
      affected: new Money(0, gross.currency).toMonetaryValue(),
      hasImpact: false,
    }
  }
  const affected = affectedAmountOf(sale, gross)
  return {
    active: gross.subtract(affected).toMonetaryValue(),
    original: gross.toMonetaryValue(),
    affected: affected.toMonetaryValue(),
    hasImpact: !affected.isZero(),
  }
}

export function summarizeJourney(journey: SaleJourney): JourneySummary {
  const charges = [journey.sale, ...journey.children]
  const currency = receivedAmountOf(journey.sale).currency
  let original = new Money(0, currency)
  let affected = new Money(0, currency)
  for (const sale of charges) {
    const received = receivedAmountOf(sale)
    if (received.currency !== currency || !wasCharged(sale)) {
      continue
    }
    const gross = Money.fromMonetaryValue(received)
    original = original.add(gross)
    affected = affected.add(affectedAmountOf(sale, gross))
  }
  const active = original.subtract(affected)
  const embeddedItemCount = journey.sale.funnelItemCount ?? 0
  const separateChargeCount = journey.children.length
  return {
    total: active.toMonetaryValue(),
    originalTotal: original.toMonetaryValue(),
    affectedTotal: affected.toMonetaryValue(),
    hasFinancialImpact: !affected.isZero(),
    itemCount: embeddedItemCount + separateChargeCount,
    embeddedItemCount,
    separateChargeCount,
    pendingStages: journey.children.filter((child) => child.status !== 'PAID').length,
  }
}

function wasCharged(sale: SaleListItem): boolean {
  return (
    sale.status === 'PAID' ||
    sale.status === 'REFUND_REQUESTED' ||
    sale.status === 'REFUNDED' ||
    sale.status === 'CHARGED_BACK'
  )
}

export function affectedAmountOf(
  sale: Pick<SaleListItem, 'status' | 'financialEvents'>,
  gross: Money,
): Money {
  const events = sale.financialEvents ?? []
  if (events.length === 0 && (sale.status === 'REFUNDED' || sale.status === 'CHARGED_BACK')) {
    return gross
  }
  let affected = new Money(0, gross.currency)
  for (const event of events) {
    if (event.kind === 'FX_LOSS' || event.kind === 'DISPUTE_WON' || event.state === 'REVERSED')
      continue
    const amount = event.chargeAffectedAmount ?? event.affectedAmount
    if (amount.currency === gross.currency) affected = affected.add(Money.fromMonetaryValue(amount))
  }
  return affected.toNumber() > gross.toNumber() ? gross : affected
}

export function groupSalesByFunnel(sales: SaleListItem[]): SaleJourney[] {
  const parentIds = new Set(
    sales.filter((sale) => sale.originSaleId === undefined).map((sale) => sale.id),
  )
  const childrenByParent = new Map<string, SaleListItem[]>()
  for (const sale of sales) {
    if (sale.originSaleId !== undefined && parentIds.has(sale.originSaleId)) {
      const list = childrenByParent.get(sale.originSaleId) ?? []
      list.push(sale)
      childrenByParent.set(sale.originSaleId, list)
    }
  }

  const journeys: SaleJourney[] = []
  for (const sale of sales) {
    if (sale.originSaleId !== undefined && parentIds.has(sale.originSaleId)) {
      continue
    }
    journeys.push({ sale, children: childrenByParent.get(sale.id) ?? [] })
  }
  return journeys
}
